/**
 * アラート設計（§9）。トリガ評価→抑制ルール→テンプレ合成。
 * LLMは使わない＝決定的（§9.1）。文言は付録Aテンプレート。
 * 通知文言は不安を煽らない表現に統一（「危険」ではなく「塗りどき」、§11.2）。
 */
import {
  ALERT_COOLDOWN_S,
  ALERT_MAX_PER_DAY,
  CMF_JUMP_DELTA,
  CMF_JUMP_WINDOW_S,
  DECAY_THRESHOLD,
  MED_SED,
  QUIET_HOUR_END,
  QUIET_HOUR_START,
  SED_PER_UVI_HOUR,
  SUN_IN_SUSTAIN_S,
  S_WARN_RATIO,
  T15_REMAIN_MIN,
} from "./constants.js";
import { decay } from "./sunscreen.js";
import type { AlertEvent, AlertType, EngineState, Ms, UserSettings, WxMinute } from "./types.js";

export interface AlertContext {
  now: Ms;
  /** 現在の太陽可視（δsun） */
  visible: boolean;
  indoor: boolean;
  wx: WxMinute;
  /** S_act の現在増加率 [SED/s] */
  sedRatePerS: number;
  settings: UserSettings;
  /** JSTの時（0..23）。抑制ルールの夜間停止判定用 */
  hourJst: number;
}

/** 警告閾値 S_warn = 0.7 × MED（§4.8） */
export function sWarn(skinType: UserSettings["skinType"]): number {
  return S_WARN_RATIO * MED_SED[skinType];
}

/** 塗りどきまでの残り時間 [min]。蓄積が止まっていれば Infinity */
export function minutesToWarn(st: EngineState, ctx: AlertContext): number {
  const remain = sWarn(ctx.settings.skinType) - st.sAct;
  if (remain <= 0) return 0;
  if (ctx.sedRatePerS <= 0) return Infinity;
  return remain / ctx.sedRatePerS / 60;
}

interface Trigger {
  type: AlertType;
  message: string;
  reasons: string[];
}

function evaluateTriggers(st: EngineState, ctx: AlertContext): Trigger[] {
  const out: Trigger[] = [];
  const reasons: string[] = [];
  if (ctx.visible) reasons.push("今日は日向");
  if (ctx.wx.cmf > 0.8) reasons.push("雲が少ない");
  if (ctx.wx.uvi >= 6) reasons.push("紫外線が強い");

  // T15: 残り時間 (S_warn − S_act)/Ė ≤ 15分
  const mins = minutesToWarn(st, ctx);
  if (mins <= T15_REMAIN_MIN) {
    const m = Math.max(1, Math.round(mins));
    out.push({
      type: "T15",
      message: `あと${m}分で塗りどきです。${reasons.slice(0, 2).join("、") || "紫外線がたまってきています"}`,
      reasons: reasons.slice(0, 2),
    });
  }

  // SUN_IN: δsun 0→1 が3分継続
  if (ctx.visible && st.sunSince !== null && ctx.now - st.sunSince >= SUN_IN_SUSTAIN_S * 1000) {
    const rate = (ctx.sedRatePerS * 3600).toFixed(2);
    out.push({ type: "SUN_IN", message: `日向に出ました。いまは${rate} SED/hです`, reasons: [] });
  }

  // CMF_JUMP: CMFが10分間で+0.3以上
  const windowStart = ctx.now - CMF_JUMP_WINDOW_S * 1000;
  const inWindow = st.cmfHistory.filter(([ts]) => ts >= windowStart);
  if (inWindow.length >= 2) {
    const minCmf = Math.min(...inWindow.map(([, c]) => c));
    if (ctx.wx.cmf - minCmf >= CMF_JUMP_DELTA) {
      out.push({
        type: "CMF_JUMP",
        message: "雲が切れました。紫外線が強くなっています",
        reasons: [],
      });
    }
  }

  // DECAY: d(t) < 0.5
  if (st.application && decay(st.application, ctx.now, ctx.wx) < DECAY_THRESHOLD) {
    out.push({
      type: "DECAY",
      message: "日焼け止めの効果が半分以下になりました",
      reasons: [],
    });
  }

  return out;
}

/** 抑制ルール（§9.2）を通過するか */
export function passesSuppression(
  st: EngineState,
  type: AlertType,
  ctx: Pick<AlertContext, "now" | "indoor" | "hourJst">,
): boolean {
  // 22:00–6:00 停止
  if (ctx.hourJst >= QUIET_HOUR_START || ctx.hourJst < QUIET_HOUR_END) return false;
  // 1日最大8件
  if (st.alertsToday >= ALERT_MAX_PER_DAY) return false;
  // 屋内は蓄積が止まるため原則発火しない（SCHEDは位置不要のため除外）
  if (ctx.indoor && type !== "SCHED") return false;
  // 同型20分クールダウン
  const last = st.lastAlertAt[type];
  if (last !== undefined && ctx.now - last < ALERT_COOLDOWN_S * 1000) return false;
  return true;
}

/**
 * トリガ評価＋抑制。発火したアラートを返し、抑制状態を st に記録する。
 * T15/DECAY 発火時は S_adh 用の仮想塗布時刻として使う（呼び出し側 engine.ts）。
 */
export function evaluateAlerts(st: EngineState, ctx: AlertContext): AlertEvent[] {
  const fired: AlertEvent[] = [];
  for (const trig of evaluateTriggers(st, ctx)) {
    if (!passesSuppression(st, trig.type, ctx)) continue;
    st.lastAlertAt[trig.type] = ctx.now;
    st.alertsToday += 1;
    fired.push({ type: trig.type, ts: ctx.now, message: trig.message, reasons: trig.reasons });
  }
  return fired;
}

/** SCHED型の文言（付録A）。Worker Cron側でも同テンプレを使う（§8.3） */
export function schedMessage(time: string, label: string, applyTime: string): string {
  return `${time}の${label}の前に、${applyTime}ごろ塗るのがおすすめです`;
}

/** 縮退モードの明示文言（§6.4・付録A） */
export const DEGRADED_MESSAGE = "気象データが取得できないため、晴天と仮定して計算しています";

export { SED_PER_UVI_HOUR };
